<template>
  <a
    v-if="href"
    :href="href"
    class="bim-btn"
    :class="[classes, activeClass]"
    :style="`
      width: ${computedWidth};
      height: ${computedHeight};
     `"
    :disabled="disabled"
  >
    <i
      v-if="icon"
      :class="icon"
      :style="`font-size: ${iconSize}px; color: ${textColor}; -webkit-text-fill-color: ${textColor}`"
    ></i>
    <span
      v-if="!this.$slots.default && !square && !rounded"
      :style="textColor ? `color: ${textColor};` : ''"
    >{{ label }}</span>
    <!-- @slot Безымянный слот. Используется для вставки html. Если в слоте что-то есть, то label игнорируется-->
    <slot></slot>
  </a>
  <button
    v-else
    class="bim-btn"
    :class="[classes, activeClass]"
    :style="style"
    @click="btnClicked"
  >
    <i
      v-if="icon"
      :class="icon"
      :style="`font-size: ${iconSize}px; color: ${textColor}; -webkit-text-fill-color: ${textColor}`"
    ></i>
    <span
      v-if="!this.$slots.default && !square && !rounded"
      :style="textColor ? `color: ${textColor};` : ''"
    >{{ label }}</span>
    <!-- @slot Безымянный слот. Используется для вставки html. Если в слоте что-то есть, то label игнорируется-->
    <slot></slot>
  </button>
</template>

<script>
export default {
  name: 'bimButton',
  props: {
    /**
     * Делает кнопку не активной
     */
    disabled: Boolean,
    /**
     * Делает кнопку не активной
     */
    makeClickOnDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Ширина кнопки. Если число без величины, будет px.
     */
    width: [String, Number],
    /**
     * Высота кнопки. Если число без величины, будет px.
     */
    height: [String, Number],
    /**
     * Ссылка кнопки
     */
    href: String,
    /**
     * Цвет кнопки
     * @values luxury, gray, darkgray, red, lightred, white
     */
    color: {
      type: String,
      default: 'primary'
    },
    /**
     * Цвет текста в любом формате. hex, rgba и т.д.
     */
    textColor: String,
    /**
     * Название класса активной ссылки/кнопки
     */
    activeClass: String,
    /**
     * Квадратная кнопка. Если включено, то label игнорируется
     */
    square: Boolean,
    /**
     * Круглая кнопка. Если включено, то label игнорируется
     */
    rounded: Boolean,
    /**
     * Кнопка с обводкой.
     * Адекватно работает только на ярких и темных кнопках.
     */
    outline: Boolean,
    /**
     * Отключение тени
     */
    noshadow: Boolean,
    /**
     * Текст кнопки
     */
    label: {
      type: String,
      default: 'Кнопка'
    },
    /**
     * Название класса иконки
     */
    icon: String,
    /**
     * Размер иконки в пикселях.
     */
    iconSize: {
      type: [String, Number],
      default: 16
    },
    /**
     * Размер кнопки.
     * @values s - маленькая кнопка, m - средняя кнопка, l - большая кнопка
     */
    size: {
      type: String,
      default: 'm',
      validator () {
        return ['s', 'm', 'l']
      }
    },
    /**
     * Изменение фона кнопки в любом формате (hex, rgb и т.д.)
     */
    background: {
      type: String
    }
  },
  computed: {
    classes () {
      return {
        'bim-btn--big': this.size === 'l',
        'bim-btn--normal': this.size === 'm',
        'bim-btn--small': this.size === 's',
        'bim-btn--square': this.square,
        'bim-btn--rounded': this.rounded,
        'bim-btn--luxury': this.color === 'luxury',
        'bim-btn--white': this.color === 'white',
        'bim-btn--gray': this.color === 'gray',
        'bim-btn--darkgray': this.color === 'darkgray',
        'bim-btn--red': this.color === 'red',
        'bim-btn--lightred': this.color === 'lightred',
        'bim-btn--outline': this.outline,
        'bim-btn--noshadow': this.noshadow,
        'bim-custom-color': this.isCustomColor,
        disabled: this.disabled
      }
    },
    computedHeight () {
      if (+this.height) {
        return this.height + 'px'
      } else {
        return this.height
      }
    },
    computedWidth () {
      if (+this.width) {
        return this.width + 'px'
      } else {
        return this.width
      }
    },

    isCustomColor () {
      return !~['primary', 'luxury', 'gray', 'darkgray', 'red', 'lightred', 'white'].indexOf(this.color)
    },

    style () {
      const style = {
        width: this.computedWidth,
        height: this.computedHeight,
        'background-color': this.background
      }

      if (this.isCustomColor) {
        style['--propscolor'] = this.color
      }

      return style
    }
  },
  methods: {
    btnClicked (e) {
      if (this.disabled && !this.makeClickOnDisabled) {
        return
      }

      this.$emit('click', e)
    }
  }
}
</script>

<style lang="scss" scoped>
*, *::after, *::before {
  box-sizing: border-box;
}

.bim-btn {
  --text-color: #ffffff;
  --primary-color: #00afa1;
  --primary-color-hover: #06baab;
  --primary-color-active: #02a497;
  --box-shadow-color: rgba(0, 175, 161, 0.2);

  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: var(--primary-color);
  color: var(--text-color);
  border-radius: 4px;
  font-weight: bold;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.36;
  box-shadow: 0 10px 10px 0 var(--box-shadow-color);
  outline: none;
  transition: 0.2s;
  cursor: pointer;
  i {
    margin-right: 4px;
    &:before {
      color: var(--text-color);
    }
  }
  &:hover {
    box-shadow: 0 30px 30px 0 var(--box-shadow-color);
    background-color: var(--primary-color-hover);
  }
  &:active {
    box-shadow: none;
    background-color: var(--primary-color-active);
  }

  // Размеры
  &--big {
    height: 54px;
    padding: 18px 60px;
  }
  &--normal {
    height: 42px;
    padding: 12px 34px;
  }
  &--small {
    height: 36px;
    font-size: 12px;
    padding: 8px 24px;
  }
  &--square {
    width: 42px;
    height: 42px;
    padding: 0;
    &.bim-btn--big {
      width: 54px;
      height: 54px;
    }
    &.bim-btn--small {
      width: 36px;
      height: 36px;
    }
    i {
      // opacity: .75;
      margin: 0;
    }
    &:hover,
    &:active {
      opacity: 1;
    }
  }
  &--rounded {
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 50%;
    &.bim-btn--big {
      width: 54px;
      height: 54px;
    }
    &.bim-btn--small {
      width: 36px;
      height: 36px;
    }
    i {
      margin: 0;
    }
  }

  // Цвета
  &--luxury {
    --primary-color: #ff9126;
    --primary-color-hover: #ff9d3e;
    --primary-color-active: #f48820;
    --box-shadow-color: rgba(255, 145, 38, 0.2);
  }
  &--white {
    --text-color: #00afa1;
    --primary-color: #ffffff;
    --primary-color-hover: #f6f6f6;
    --primary-color-active: #eeeeee;
    --box-shadow-color: none;
  }
  &--gray {
    --text-color: #4d4d4d;
    --primary-color: #eeeeee;
    --primary-color-hover: #f6f6f6;
    --primary-color-active: #e6e6e6;
    --box-shadow-color: none;
  }
  &--darkgray {
    --text-color: #ffffff;
    --primary-color: #4d4d4d;
    --primary-color-hover: #5b5b5b;
    --primary-color-active: #444444;
    --box-shadow-color: none;
  }
  &--red {
    --primary-color: #e44545;
    --primary-color-hover: #f65959;
    --primary-color-active: #d43838;
    --box-shadow-color: rgba(228, 69, 69, 0.2);
  }
  &--lightred {
    --text-color: #e44545;
    --primary-color: #ffffff;
    --primary-color-hover: rgba(228, 69, 69, 0.1);
    --primary-color-active: rgba(228, 69, 69, 0.25);
    --box-shadow-color: none;
  }

  // Кнопка с обводкой
  &--outline {
    position: relative;
    top: -1px;
    border: 1px solid var(--primary-color);
    background: transparent;
    color: var(--primary-color);
    --box-shadow-color: none;
    transition: all .2s, color 0s;
    &:hover {
      border: 1px solid var(--primary-color-hover);
      color: #fff;
    }
  }
  // Убираем тень
  &--noshadow {
    --box-shadow-color: none;
  }
}

.bim-custom-color,
.bim-custom-color:hover
{
  background-color: var(--propscolor);
  box-shadow: none;
}

.bim-custom-color:hover {
  opacity: 0.9;
}

// Отключаем кнопку
.bim-btn.disabled {
  opacity: 0.4;
  box-shadow: none;
}
</style>
